import React, { useRef, useState, useEffect, useMemo } from "react";
import Map, { Marker, NavigationControl, Popup } from "react-map-gl";
import { Source, Layer } from "react-map-gl";
import useSupercluster from "use-supercluster";
import { Button, Form, Modal } from "antd";
import { useSelector } from "react-redux";
import * as turf from "@turf/turf";
import bbox from "@turf/bbox";
import { sample, find, debounce } from "lodash";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { SyncOutlined } from '@ant-design/icons';

import "./Main.scss";
import "mapbox-gl/dist/mapbox-gl.css";
import markerIcons from "components/Markers/markerIcons";
import {
  EditAddressIcon,
  EditLatLngIcon,
  LocationCenterIcon,
} from "components/Icons";
import { PanToolIcon, PolylineIcon, CopyIcon } from "components/Icons";
import LocationForm from "components/Forms/Locations/Location";
import SubZoneForm from "components/Forms/SubZones/SubZone";
import { COLOR_DEFAULT, COLORS_SUBZONE } from "constants";

import DrawControl from "./DrawControl";
import ConfirmAddress from "./ConfirmAddress";


export const MAP_CONFIG = {
  mapboxApiAccessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN,
};

export const MAP_STYLES = {
  standard: "mapbox://styles/theprof/cl8nwpgtl004z15mi7tjvhoaf",
  satellite: "mapbox://styles/mapbox/satellite-streets-v12"
}

const navControlStyle = {
  right: 100,
  top: 200,
};

const MapboxMain = ({
  zone,
  circle,
  line,
  locations,
  locationClicked,
  setLocationClicked,
  subZoneClicked,
  setSubZoneClicked,
  subZonesChecked,
  menuSelecled,
  setStopsInNewZones,
  stopsInNewZones,
  isConfirmedAddress,
  onSetIsConfirmedAddress,
}) => {
  const mapRef = useRef();
  const [fitBounded, setFitBounded] = useState(false);
  const [showDraw, setShowDraw] = useState(false);
  const [copied, setCopied] = useState({});
  const [style, setStyle] = useState(MAP_STYLES.standard);
  const [viewport, setViewport] = useState({
    width: "100%",
    height: "100%",
    zoom: 10,
  });

  const points = useMemo(
    () =>
      locations
        ?.filter((l) =>  {
          const isSubZoneSelected = menuSelecled === "sub-zones" && (!!subZonesChecked.find(sc => sc._id === l.subZone?._id) || !l.subZone);
          const isMenuNotSubZone = menuSelecled !== "sub-zones";
          return isSubZoneSelected || isMenuNotSubZone
        })
        ?.map(({ _id, orderNo, lat, lng, type, color }) => ({
          type: "Feature",
          properties: {
            cluster: false,
            orderNo,
            locationType: type,
            locationColor: color,
            locationId: _id,
          },
          geometry: {
            type: "Point",
            coordinates: [lng, lat],
          },
        })),
    // eslint-disable-next-line
    [locations]
  );

  const bounds = mapRef.current
    ? mapRef.current.getMap().getBounds().toArray().flat()
    : null;

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom: viewport.zoom,
    options: { radius: 20, maxZoom: 20 },
  });

  const [form] = Form.useForm();
  const [formSub] = Form.useForm();

  const { isFetching } = useSelector((state) => state.location);
  const { isFetching: isFetchingSub } = useSelector((state) => state.subZone);
  const [state, setState] = useState({
    visibleModal: false,
    action: "",
    location: null,
  });

  const [stateSub, setStateSub] = useState({
    visibleModal: false,
    action: "",
    subZone: null,
  });

  useEffect(() => {
    if (locations.length > 2 && mapRef.current && !fitBounded) {
      setFitBounded(true);
      // Filter locations with ptsWithin property
      const validLocations = locations.filter((l) => l.ptsWithin);
      if (validLocations.length >= 2) {
        const points = validLocations.map((l) => [l.lng, l.lat]);
        const feature = turf.lineString(points);
        const [minLng, minLat, maxLng, maxLat] = bbox(feature);
        mapRef.current.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          { padding: 100, duration: 500 }
        );
      }
    }
  }, [locations, fitBounded]);

  useEffect(() => {
    if (locationClicked) {
      mapRef.current.flyTo({
        center: [locationClicked.lng, locationClicked.lat],
        duration: 500,
      });
    }
  }, [locationClicked]);

  useEffect(() => {
    if (subZoneClicked) {
      formSub.resetFields();
      const params = {
        ...subZoneClicked,
        startAddress: {
          address: subZoneClicked.startAddress,
          latLng: {
            lat: subZoneClicked.startLat,
            lng: subZoneClicked.startLng,
          },
        },
        endAddress: {
          address: subZoneClicked.endAddress,
          latLng: { lat: subZoneClicked.endLat, lng: subZoneClicked.endLng },
        },
      };
      if (subZoneClicked.points.length > 0) {
        mapRef.current.flyTo({
          center: subZoneClicked.points[0],
          duration: 500,
        });
      }
      formSub.setFieldsValue(params);
      setStateSub({
        ...stateSub,
        action: "edit",
        subZone: subZoneClicked,
        visibleModal: true,
      });
    }
    // eslint-disable-next-line
  }, [subZoneClicked]);

  const onClosePopup = () => {
    setLocationClicked(null);
    onSetIsConfirmedAddress(false);
  };

  const handleCancel = () => {
    setState({ ...state, action: "", location: null, visibleModal: false });
    form.resetFields();
  };

  const changeStyleMap = () => {
    if (style === MAP_STYLES.standard) {
      setStyle(MAP_STYLES.satellite);
    } else {
      setStyle(MAP_STYLES.standard)
    }
  }

  const handleCancelSub = () => {
    setStateSub({
      ...stateSub,
      action: "",
      subZone: null,
      visibleModal: false,
    });
    formSub.resetFields();
    setShowDraw(false);
    setSubZoneClicked(null);
    setStopsInNewZones(undefined);
  };

  const handleClickAction = (item, action) => {
    form.resetFields();
    if (action === "new") {
      setState({
        ...state,
        action: action,
        location: null,
        visibleModal: true,
      });
    } else {
      const params = {
        ...item,
        currentAddress: item.address,
        location: {
          address: item.address,
          latLng: { lat: item.lat, lng: item.lng },
        },
      };
      form.setFieldsValue(params);
      setState({
        ...state,
        action: action,
        location: item,
        visibleModal: true,
      });
    }
  };

  const onCreate = async (e) => {
    const points = e.features[0].geometry.coordinates[0];
    const polygon = turf.polygon([points]);
    const locationInside = locations
      ?.filter(({ noLatLng }) => !noLatLng)
      ?.filter((l) => {
        const point = turf.point([l.lng, l.lat]);
        return turf.booleanPointInPolygon(point, polygon);
      });
    setStopsInNewZones && setStopsInNewZones(locationInside.length);
    formSub.setFieldsValue({ points });
  };

  const handleShowDraw = (showDrawT) => {
    setShowDraw(showDrawT);
    if (showDrawT) {
      formSub.setFieldsValue({ color: sample(COLORS_SUBZONE) });
      setStateSub({ ...stateSub, action: "new", subZone: null });
    }
  };

  const getFeatureLineSubZones = () => {
    return {
      type: "FeatureCollection",
      features: subZonesChecked.map((s, index) => {
        const f = turf.lineString(s.points);
        const properties = {
          ...f.properties,
          color: index,
        };
        return { ...f, properties };
      }),
    };
  };

  const getFeatureFillSubZones = () => {
    return {
      type: "FeatureCollection",
      features: subZonesChecked.map((s, index) => {
        const f = turf.polygon([[...s.points]]);
        const properties = {
          ...f.properties,
          color: index,
        };
        return { ...f, properties };
      }),
    };
  };

  const handleClusterClick = (cluster) => {
    const [longitude, latitude] = cluster.geometry.coordinates;
    const expansionZoom = Math.min(
      Math.max(supercluster.getClusterExpansionZoom(cluster.id), 12),
      20
    );
    mapRef.current.flyTo({
      center: [longitude, latitude],
      zoom: expansionZoom,
      duration: 500,
    });
  };

  return (
    <>
      <div className="MapboxMain">
        <Map
          ref={mapRef}
          initialViewState={{
            ...viewport,
            latitude: zone.centerLat,
            longitude: zone.centerLng,
          }}
          mapStyle={style}
          {...MAP_CONFIG}
          onMove={(event) => {
            debounce(
              (e) => {
                setViewport({ ...viewport, ...(e?.viewState || {}) });
              },
              100,
              { leading: true, maxWait: 1000 }
            )(event);
          }}
          // maxZoom={20}
        >
          <Source
            id="fill"
            type="geojson"
            data={
              menuSelecled === "addresses" ? circle : getFeatureFillSubZones()
            }
          >
            <Layer
              type="fill"
              paint={{
                "fill-color":
                  menuSelecled === "addresses"
                    ? "#55a2e7"
                    : {
                        property: "color",
                        stops: [
                          ...subZonesChecked.map((s, index) => [
                            index,
                            `#${s.color || COLOR_DEFAULT}`,
                          ]),
                        ],
                      },
                "fill-opacity": 0.2,
              }}
            />
          </Source>

          <Source
            id="line"
            type="geojson"
            data={
              menuSelecled === "addresses" ? line : getFeatureLineSubZones()
            }
          >
            <Layer
              type="line"
              paint={{
                "line-color":
                  menuSelecled === "addresses"
                    ? "#55a2e7"
                    : {
                        property: "color",
                        stops: [
                          ...subZonesChecked.map((s, index) => [
                            index,
                            `#${s.color || COLOR_DEFAULT}`,
                          ]),
                        ],
                      },
                "line-width": 1,
              }}
            />
          </Source>

          {clusters.map((cluster) => {
            const [longitude, latitude] = cluster.geometry.coordinates;
            const {
              cluster: isCluster,
              point_count: pointCount,
              locationId,
              locationType,
              locationColor,
              orderNo,
            } = cluster.properties;
            const clusterMarkerSize = parseInt(pointCount / 300, 10);

            if (isCluster) {
              return (
                <Marker
                  key={`cluster-${cluster.id}`}
                  latitude={latitude}
                  longitude={longitude}
                  onClick={() => handleClusterClick(cluster)}
                >
                  <div
                    className="cluster-marker"
                    style={{
                      width: `${26 + clusterMarkerSize}px`,
                      height: `${26 + clusterMarkerSize}px`,
                    }}
                  >
                    {pointCount}
                  </div>
                </Marker>
              );
            }

            const urlIcon = markerIcons[locationType][locationColor];
            return (
              <Marker
                key={locationType + locationId}
                latitude={latitude}
                longitude={longitude}
                style={{
                  zIndex:
                    locationClicked?._id === cluster.properties._id
                      ? orderNo * 0.00001
                      : 0,
                }}
              >
                <div
                  className="marker"
                  onClick={() =>
                    setLocationClicked(find(locations, { _id: locationId }))
                  }
                >
                  <img src={urlIcon} alt="" />
                  {orderNo > 0 && (
                    <div
                      className="marker-info"
                      style={{
                        color: "white",
                        fontSize: "10px",
                        fontWeight: "bold",
                      }}
                    >
                      {orderNo}
                    </div>
                  )}
                </div>
              </Marker>
            );
          })}
          {locationClicked && !isConfirmedAddress && (
            <Popup
              key={`location-${locationClicked._id}`}
              longitude={locationClicked.lng}
              latitude={locationClicked.lat}
              closeOnClick={false}
              onClose={onClosePopup}
              anchor="bottom"
              offset={16}
            >
              <div className="location-popup">
                <div
                  className={`name ${locationClicked.orderNo === 0 && "zone"}`}
                >
                  <div className="left">
                    <div
                      className="icon"
                      style={{
                        border: `1px solid #${locationClicked.color}`,
                        color: `#${locationClicked.color}`,
                      }}
                    >
                      {locationClicked.orderNo !== 0 ? (
                        locationClicked.orderNo
                      ) : (
                        <LocationCenterIcon />
                      )}
                    </div>
                    {locationClicked.address}
                    <CopyToClipboard
                      text={locationClicked.address}
                      onCopy={() =>
                        setCopied({ ...copied, [locationClicked._id]: true })
                      }
                      className={`CopyToClipboard ${
                        copied[locationClicked._id] && "Copied"
                      }`}
                    >
                      <CopyIcon />
                    </CopyToClipboard>
                  </div>
                  <div className="right">{locationClicked.freeTextField1}</div>
                </div>
                {locationClicked.orderNo !== 0 && (
                  <>
                    <div className="notes">{locationClicked.note}</div>
                    <div className="actions">
                      <Button
                        className="btn-action"
                        onClick={() =>
                          handleClickAction(locationClicked, "enter_address")
                        }
                      >
                        Redigera adress
                        <EditAddressIcon />
                      </Button>
                      <Button
                        className="btn-action"
                        onClick={() =>
                          handleClickAction(locationClicked, "enter_latlng")
                        }
                      >
                        Redigera Lat/long
                        <EditLatLngIcon />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </Popup>
          )}
          {menuSelecled === "sub-zones" && (
            <>
              {showDraw && (
                <DrawControl
                  position="top-left"
                  displayControlsDefault={false}
                  defaultMode="draw_polygon"
                  onCreate={onCreate}
                  onUpdate={onCreate}
                  onDelete={onCreate}
                />
              )}
              <div className="ToolbarDraw">
                <div className="mapboxgl-ctrl-group button-group--horizontal">
                  <button
                    className={`mapbox-ctrl-draw-btn ${
                      !showDraw ? "active" : ""
                    }`}
                    onClick={() => handleShowDraw(false)}
                  >
                    <PanToolIcon />
                  </button>
                  <button
                    className={`mapbox-ctrl-draw-btn ${
                      showDraw ? "active" : ""
                    }`}
                    onClick={() => handleShowDraw(true)}
                  >
                    <PolylineIcon />
                  </button>
                </div>
              </div>
            </>
          )}
          <NavigationControl style={navControlStyle} />
          <button className="change-style-map" onClick={changeStyleMap}>
            <SyncOutlined />
          </button>
        </Map>
        <Modal
          open={state.visibleModal}
          onCancel={handleCancel}
          footer={[]}
          width={408}
        >
          <LocationForm
            zone={zone}
            circle={circle}
            form={form}
            state={state}
            isFetching={isFetching}
            handleCancel={handleCancel}
            onClosePopup={onClosePopup}
          />
        </Modal>
        <Modal
          open={stateSub.visibleModal}
          onCancel={handleCancelSub}
          footer={[]}
          width={408}
        >
          <SubZoneForm
            zone={zone}
            form={formSub}
            state={stateSub}
            isFetching={isFetchingSub}
            handleCancel={handleCancelSub}
            onClosePopup={onClosePopup}
          />
        </Modal>
        <Modal
          open={locationClicked && isConfirmedAddress}
          onCancel={onClosePopup}
          footer={[]}
          width={816}
        >
          <ConfirmAddress
            location={locationClicked}
            zone={zone}
            circle={circle}
            isFetching={isFetching}
            onClosePopup={onClosePopup}
          />
        </Modal>
      </div>
      {showDraw && (
        <div className="RightSubZoneForm">
          <SubZoneForm
            zone={zone}
            form={formSub}
            state={stateSub}
            stopsInNewZones={stopsInNewZones}
            isFetching={isFetchingSub}
            handleCancel={handleCancelSub}
            onClosePopup={onClosePopup}
          />
        </div>
      )}
    </>
  );
};

export default MapboxMain;
